import type { ReactNode } from 'react';
import type { ImageAsset } from '@/assets';
import { strings } from '@/content/strings';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import { Frame } from './Frame';
import { RoundIconButton } from './RoundIconButton';
import { Surface } from './Surface';
import styles from './WordCueCard.module.css';

/**
 * The card while the coach shows how to make the sound (a placement cue): the mouth picture in
 * place of the word's picture, and the step being said in place of the instruction.
 */
export interface CoachingView {
  /** Stable id of the cue's picture; it flips in again when it changes. */
  id: string;
  picture: ReactNode;
  /** The cue step being said, or what to look at in the picture between steps. */
  line: string;
  /** The child makes this sound now ("ee"), as a step of the cue. */
  trying?: string | null;
  /** Hear the cue again. */
  onReplay: () => void;
}

interface WordCueCardProps {
  /** Stable id; the card re-deals (animates in) when it changes. */
  id: string;
  word: string;
  picture: ImageAsset;
  /** Instruction under the word. */
  hint?: string;
  /** Plays the word; the speaker button is hidden without it. */
  onHear?: () => void;
  /** A riddle not yet answered: the picture and the word stay hidden. */
  mystery?: boolean;
  /** "Show me how": a cue with its mouth diagram and a model; hidden without it. */
  onHelp?: () => void;
  /** The coach is showing how to make the sound (see CoachingView). */
  coaching?: CoachingView | null;
}

/**
 * A framed cue card: picture, the word to say, an instruction and a speaker button. While the
 * coach shows how to make the sound, the same card turns to the mouth picture and the steps, so
 * the help is part of the practice, not a window over it.
 */
export function WordCueCard({
  id,
  word,
  picture,
  hint = strings.speech.sayThisWord,
  onHear,
  mystery = false,
  onHelp,
  coaching = null,
}: WordCueCardProps) {
  const shown = mystery && !coaching ? '?' : word;
  return (
    <Frame className={styles.frame}>
      <Surface variant="glassPanel" className={cx(styles.card, coaching && styles.coaching)}>
        {coaching ? (
          <div key={coaching.id} className={cx(styles.picture, styles.mouth)}>
            {coaching.picture}
          </div>
        ) : (
          <div key={`${id}:${String(mystery)}`} className={styles.picture}>
            {mystery ? (
              <span className={styles.mystery} role="img" aria-label={strings.speech.mysteryWord}>
                ?
              </span>
            ) : (
              <img
                src={picture.url}
                width={picture.width}
                height={picture.height}
                alt=""
                draggable={false}
              />
            )}
          </div>
        )}
        <div className={styles.text} style={cssVars({ '--chars': Math.max(shown.length, 4) })}>
          <p key={`${id}:${String(mystery)}`} className={styles.word} data-testid="cue-word">
            {shown}
          </p>
          {coaching ? (
            <p key={coaching.line} className={cx(styles.hint, styles.step)} aria-live="polite">
              {coaching.trying ? (
                <span className={styles.turn}>{strings.hint.yourTurn(coaching.trying)}</span>
              ) : (
                coaching.line
              )}
            </p>
          ) : (
            <p className={styles.hint}>{hint}</p>
          )}
        </div>
        <div className={styles.buttons}>
          {coaching ? (
            <RoundIconButton
              icon="speaker"
              label={strings.hint.replay}
              size={52}
              onClick={coaching.onReplay}
            />
          ) : (
            <>
              {onHear && !mystery && (
                <RoundIconButton
                  icon="speaker"
                  label={strings.speech.hearWord}
                  size={52}
                  onClick={onHear}
                />
              )}
              {onHelp && (
                <RoundIconButton
                  icon="help"
                  label={strings.speech.showMeHow}
                  size={52}
                  onClick={onHelp}
                />
              )}
            </>
          )}
        </div>
      </Surface>
    </Frame>
  );
}
